import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag, ExternalLink } from "lucide-react";
import { useCart } from "@/lib/cart";

function formatMoney(amount: string, currencyCode: string) {
  const n = Number(amount);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode || "USD",
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

export function CartDrawer() {
  const { isOpen, closeCart, checkout, updateItem, removeItem } = useCart();

  const lineItems = checkout?.lineItems ?? [];
  const subtotal = checkout?.subtotalPrice;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && closeCart()}>
      <SheetContent
        side="right"
        className="flex w-full flex-col border-l-border bg-card text-card-foreground sm:max-w-md"
      >
        <SheetHeader className="mb-4">
          <SheetTitle className="text-center text-xl font-semibold">Your Cart</SheetTitle>
        </SheetHeader>

        {lineItems.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary">
              <ShoppingBag className="h-7 w-7 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">Your cart is empty.</p>
          </div>
        ) : (
          <>
            <ul className="flex-1 space-y-4 overflow-y-auto pr-1">
              {lineItems.map((li) => (
                <li key={li.id} className="flex gap-3 rounded-lg border border-border p-3">
                  {li.variant?.image?.src ? (
                    <img
                      src={li.variant.image.src}
                      alt={li.title}
                      className="h-20 w-20 flex-shrink-0 rounded object-cover"
                    />
                  ) : (
                    <div className="h-20 w-20 flex-shrink-0 rounded bg-secondary" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">{li.title}</p>
                        {li.variant?.title && li.variant.title !== "Default Title" && (
                          <p className="text-xs text-muted-foreground">{li.variant.title}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(li.id)}
                        className="text-muted-foreground transition-colors hover:text-foreground"
                        aria-label="Remove item"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="inline-flex items-center rounded-md border border-border">
                        <button
                          type="button"
                          onClick={() => updateItem(li.id, Math.max(0, li.quantity - 1))}
                          className="px-2 py-1 text-muted-foreground hover:text-foreground"
                          aria-label="Decrease"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="w-8 text-center text-sm">{li.quantity}</span>
                        <button
                          type="button"
                          onClick={() => updateItem(li.id, li.quantity + 1)}
                          className="px-2 py-1 text-muted-foreground hover:text-foreground"
                          aria-label="Increase"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      {li.variant?.price && (
                        <span className="text-sm font-semibold">
                          {formatMoney(
                            (Number(li.variant.price.amount) * li.quantity).toString(),
                            li.variant.price.currencyCode,
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-4 border-t border-border pt-4">
              <div className="mb-3 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-semibold">
                  {subtotal ? formatMoney(subtotal.amount, subtotal.currencyCode) : "—"}
                </span>
              </div>
              <Button
                asChild
                className="w-full"
                size="lg"
                disabled={!checkout?.webUrl}
              >
                <a href={checkout?.webUrl} target="_blank" rel="noopener noreferrer">
                  Checkout
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                </a>
              </Button>
              <p className="mt-2 text-center text-xs text-muted-foreground">
                Shipping & taxes calculated at checkout.
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
